import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  NOT_READY_REASON_LABELS,
  campaignPlacesCalls,
  type Audience,
  type CampaignSpec,
  type ReferralReadiness,
} from '@vagusx/shared';
import type { AudienceCatalogEntry } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAudiencePreview } from '@/hooks/use-outreach';
import { useDebounce } from '@/hooks/use-debounce';
import { AudienceSection } from './AudienceSection';
import { readinessOf, setReadiness } from './spec';

/**
 * Who (ADR-318, P11): who qualifies, when they are ready to call, and how many
 * are ready right now.
 *
 * Qualifying and ready are two questions on purpose. A referral can qualify —
 * open, nothing booked — and still be missing what Iris needs to call it. Those
 * get no run; they wait in the queue's Not ready lane with their reason and join
 * on their own once it is fixed.
 */
export function WhoStep({
  spec,
  onSpecChange,
  catalog,
}: {
  spec: CampaignSpec;
  onSpecChange: (spec: CampaignSpec) => void;
  catalog: AudienceCatalogEntry[] | undefined;
}): React.ReactElement {
  const readiness = readinessOf(spec);
  const calls = campaignPlacesCalls(spec);
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="min-w-0 space-y-6 lg:col-span-2">
        <section className="space-y-3">
          <h3 className="text-sm font-medium">Who qualifies</h3>
          <AudienceSection
            audience={spec.audience}
            placesCalls={calls}
            onChange={(audience) => onSpecChange({ ...spec, audience })}
            catalog={catalog}
          />
        </section>

        {readiness && (
          <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-sm font-medium">Ready to call when</h3>
              <span className="text-xs text-muted-foreground">all of these are true</span>
            </div>
            <ReadinessSection
              readiness={readiness}
              calls={calls}
              onChange={(patch) => onSpecChange(setReadiness(spec, patch))}
            />
            <p className="text-xs text-muted-foreground">
              Checked from your referrals, patients and calendar every few minutes, and again before
              every call, so a referral booked a minute ago is never called.
            </p>
          </section>
        )}
      </div>

      {/* The audience as authored, plus the one fact the server needs to count
          it as the reconciler will: whether the campaign places calls. */}
      <RightNowPanel audience={spec.audience} placesCalls={calls} />
    </div>
  );
}

/**
 * What the date-of-birth row says. A campaign that calls always requires one
 * (ADR-333), so the row is a switch only for a campaign with no call step — and
 * a choice saved earlier is NAMED while it is overridden, so it never comes back
 * into force unseen when the last call step is removed.
 */
function dobHint(calls: boolean, saved: boolean): string {
  if (!calls) {
    return 'Only a call needs it. Once this campaign has a call step it is always required.';
  }
  const always =
    'Iris confirms identity with it before saying why she called, so she never calls without one.';
  return saved
    ? always
    : `${always} Your saved choice, not required, applies again only if every call step is removed.`;
}

/**
 * The "Ready to call when" rows. Name and a phone we can dial are always
 * required and have no switch: without them there is nobody to ask for and
 * nothing to dial.
 */
export function ReadinessSection({
  readiness,
  calls = false,
  onChange,
}: {
  readiness: ReferralReadiness;
  /**
   * The campaign has a call step. Iris verifies whoever answers against the
   * date of birth and never calls without one, so it is not a choice then.
   */
  calls?: boolean;
  onChange: (patch: Partial<ReferralReadiness>) => void;
}): React.ReactElement {
  const [horizonDraft, setHorizonDraft] = useState<string | null>(null);
  return (
    <div className="divide-y rounded-lg border">
      <Rule title="Patient name" hint="Iris has to know who she is asking for.">
        <Badge variant="secondary">Always</Badge>
      </Rule>
      <Rule
        title="A phone number we can dial"
        hint="A number that does not parse as dialable does not count."
      >
        <Badge variant="secondary">Always</Badge>
      </Rule>
      <Rule
        title="Date of birth"
        hint={dobHint(calls, readiness.dob)}
        {...(calls ? {} : { htmlFor: 'ready-dob' })}
      >
        {calls ? (
          <Badge variant="secondary">Always</Badge>
        ) : (
          <Toggle id="ready-dob" on={readiness.dob} onChange={(dob) => onChange({ dob })} />
        )}
      </Rule>
      <Rule
        title="A provider can be offered"
        hint="The referral matches at least one provider, so there is someone to book with."
        htmlFor="ready-pool"
      >
        <Toggle id="ready-pool" on={readiness.pool} onChange={(pool) => onChange({ pool })} />
      </Rule>
      <Rule
        title="Those providers have open slots"
        hint={`At least one opening in the next ${readiness.slotHorizonDays} days, from the last calendar sync.`}
        htmlFor="ready-slots"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="ready-horizon" className="sr-only">
              Days ahead to look for openings
            </Label>
            <Input
              id="ready-horizon"
              type="number"
              min={3}
              max={90}
              className="h-8 w-16"
              disabled={!readiness.slots}
              // What is being typed is held here until it is a valid number,
              // so "14" can be typed through its first digit; blur drops an
              // invalid draft back to the stored value.
              value={horizonDraft ?? readiness.slotHorizonDays}
              onChange={(e) => {
                setHorizonDraft(e.target.value);
                const days = Number(e.target.value);
                // The schema's bounds; a value outside them would make the
                // campaign unpublishable from a field that looked fine.
                if (e.target.value !== '' && Number.isInteger(days) && days >= 3 && days <= 90) {
                  onChange({ slotHorizonDays: days });
                }
              }}
              onBlur={() => setHorizonDraft(null)}
            />
            <span className="text-xs text-muted-foreground">days</span>
          </div>
          <Toggle id="ready-slots" on={readiness.slots} onChange={(slots) => onChange({ slots })} />
        </div>
      </Rule>
    </div>
  );
}

function Rule({
  title,
  hint,
  htmlFor,
  children,
}: {
  title: string;
  hint: string;
  htmlFor?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        {htmlFor ? (
          <Label htmlFor={htmlFor} className="text-sm font-medium">
            {title}
          </Label>
        ) : (
          <span className="text-sm font-medium">{title}</span>
        )}
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Toggle({
  id,
  on,
  onChange,
}: {
  id: string;
  on: boolean;
  onChange: (on: boolean) => void;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 text-right text-xs text-muted-foreground">
        {on ? 'Required' : 'Not required'}
      </span>
      <Switch id={id} checked={on} onCheckedChange={onChange} />
    </div>
  );
}

/**
 * How many qualify, and how many of them are ready, right now — the same
 * evaluation the reconciler runs, not an estimate. Each not-ready referral is
 * counted under its FIRST missing piece, so the reasons add up to the total.
 */
export function RightNowPanel({
  audience,
  placesCalls,
}: {
  audience: Audience;
  /** The campaign has a call step; the server then requires a date of birth. */
  placesCalls: boolean;
}): React.ReactElement {
  // A population-scale query: debounced like the audience count, and on the
  // same cache key — the same audience and the same `placesCalls` — so the two
  // never disagree and one request serves both.
  const debounced = useDebounce(audience, 400);
  const { data, isFetching, isError } = useAudiencePreview(debounced, placesCalls);
  const hasReadiness = data?.ready !== undefined && data.notReady !== undefined;

  return (
    <aside className="h-fit space-y-4 rounded-lg border p-4 lg:sticky lg:top-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Right now</h3>
        {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>

      {isError && <p className="text-sm text-destructive">Could not count this audience.</p>}

      {data && (
        <>
          {data.aboveCeiling && (
            <p className="flex items-start gap-1.5 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              More patients match than a campaign can take. Narrow the audience before launching.
            </p>
          )}

          <div className="flex items-end gap-5">
            {hasReadiness && (
              <>
                <Count value={data.ready!} label="ready to call" emphasis />
                <Count value={data.notReady!} label="not ready" />
              </>
            )}
            <Count value={data.patients} label="qualify" />
          </div>

          {hasReadiness && (data.notReadyReasons?.length ?? 0) > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Why they are not ready
              </p>
              <ul className="space-y-1">
                {data.notReadyReasons!.map((r) => (
                  <li key={r.reason} className="flex justify-between gap-3 text-sm">
                    <span>{NOT_READY_REASON_LABELS[r.reason]}</span>
                    <span className="tabular-nums text-muted-foreground">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasReadiness && (data.notReady ?? 0) > 0 && (
            <p className="text-xs text-muted-foreground">
              They wait in the queue's Not ready lane with their reason, and join on their own once
              it is fixed. What else to do about them is not decided yet.
            </p>
          )}
        </>
      )}
    </aside>
  );
}

function Count({
  value,
  label,
  emphasis,
}: {
  value: number;
  label: string;
  emphasis?: boolean;
}): React.ReactElement {
  return (
    <div>
      <div
        className={
          emphasis ? 'text-3xl font-semibold tabular-nums' : 'text-xl font-semibold tabular-nums'
        }
      >
        {value.toLocaleString()}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
